import { DEFAULT_ROLE_PERMISSIONS_MAP, ADMINISTRATOR_DEFAULT_PERMISSIONS } from './defaultRolePermissions';
import { authFetch } from '@/lib/auth-fetch';
import { getApiUrl } from '@/lib/api-config';

// Types for UserRole API
export interface UserRoleListItem {
  id: number;
  name: string;
  description?: string;
  isSystem: boolean;
  isActive: boolean;
  userCount: number;
  createdAt: string;
}

export interface UserRolePermissions {
  reportAccessPermissions?: {
    utilisationReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
    utilisationForecastReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
    availableCapacityReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
    resourceAllocationReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
    projectStaffingReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
    benchReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
    auditLogReport?: { enabled: boolean; viewAccess: string; allowExport: boolean };
  };
  resourceDirectoryPermissions?: {
    viewDirectory: boolean;
    viewDetails: boolean;
  };
  integrationsPermissions?: {
    viewIntegrations: boolean;
    manageIntegrations: boolean;
  };
  companyManagementPermissions?: {
    viewCompanies: boolean;
    manageCompanies: boolean;
  };
  settingsPermissions?: {
    viewGeneral: boolean;
    viewTimezone: boolean;
    viewBookingTypeSetup: boolean;
    viewGroupSetup: boolean;
    viewRolesPermissions: boolean;
    viewUserSetup: boolean;
  };
  navigationPermissions?: {
    viewDashboard: boolean;
    viewHeatmap: boolean;
    viewOpenDemand: boolean;
    viewProjects: boolean;
  };
}

export interface UserRoleEmployee {
  id: number;
  name: string;
  email: string;
  roleName?: string;
  status: string;
  assignedAt: string;
}

export interface UserRole {
  id: number;
  organisationId: string;
  companyId?: string;
  name: string;
  description?: string;
  isSystem: boolean;
  isActive: boolean;
  userCount: number;
  createdAt: string;
  createdBy: number;
  createdByName?: string;
  updatedAt?: string;
  updatedBy?: number;
  updatedByName?: string;
  permissions?: UserRolePermissions;
  users?: UserRoleEmployee[];
}

export interface CreateUserRoleDto {
  name: string;
  description?: string;
  isActive?: boolean;
  permissions?: UserRolePermissions;
}

export interface UpdateUserRoleDto {
  name: string;
  description?: string;
  isActive?: boolean;
  permissions?: UserRolePermissions;
}

export interface MyPermissionsResponse {
  roleId: number | null;
  roleName: string | null;
  isAdminFallback: boolean;
  permissions: UserRolePermissions;
}

export type DataMode = 'demo' | 'live';

// API helper — mirrors the fetchWithErrorHandling pattern from api.ts
const getApiBaseUrl = (): string => {
  const configuredUrl = getApiUrl();
  if (configuredUrl) return configuredUrl.replace(/\/$/, '');
  return import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5000';
};

async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const url = `${getApiBaseUrl()}/api${endpoint}`;

  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const response = await authFetch(url, config);

  if (!response.ok) {
    let errorMessage = `API request failed: ${response.statusText}`;
    try {
      const errorBody = await response.clone().json();
      errorMessage = errorBody.message || errorBody.error || errorMessage;
    } catch {
      // use default message
    }
    throw new Error(errorMessage);
  }

  if (response.status === 204) return {} as T;
  return response.json() as Promise<T>;
}

// Real API service — calls the backend UserRoles API
const realUserRoleService = {
  async getAllUserRoles(): Promise<UserRoleListItem[]> {
    return apiFetch<UserRoleListItem[]>('/UserRoles');
  },

  async getUserRoleById(id: number): Promise<UserRole> {
    return apiFetch<UserRole>(`/UserRoles/${id}`);
  },

  async createUserRole(dto: CreateUserRoleDto): Promise<UserRole> {
    return apiFetch<UserRole>('/UserRoles', {
      method: 'POST',
      body: JSON.stringify(dto),
    });
  },

  async updateUserRole(id: number, dto: UpdateUserRoleDto): Promise<UserRole> {
    return apiFetch<UserRole>(`/UserRoles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    });
  },

  async deleteUserRole(id: number): Promise<void> {
    await apiFetch<void>(`/UserRoles/${id}`, { method: 'DELETE' });
  },

  async getEmployeesByRoleId(roleId: number): Promise<UserRoleEmployee[]> {
    return apiFetch<UserRoleEmployee[]>(`/UserRoles/${roleId}/employees`);
  },

  async assignEmployeeToRole(roleId: number, employeeId: number): Promise<void> {
    await apiFetch<void>(`/UserRoles/${roleId}/employees/${employeeId}`, { method: 'POST' });
  },

  async removeEmployeeFromRole(roleId: number, employeeId: number): Promise<void> {
    await apiFetch<void>(`/UserRoles/${roleId}/employees/${employeeId}`, { method: 'DELETE' });
  },

  async transferEmployeeToRole(fromRoleId: number, toRoleId: number, employeeId: number): Promise<void> {
    await apiFetch<void>(`/UserRoles/${fromRoleId}/employees/${employeeId}/transfer`, {
      method: 'POST',
      body: JSON.stringify({ toRoleId }),
    });
  },

  async getMyPermissions(): Promise<MyPermissionsResponse> {
    return apiFetch<MyPermissionsResponse>('/UserRoles/me/permissions');
  },
};

// Mock data for demo mode
const defaultMockUserRoles: UserRoleListItem[] = [
  {
    id: 1,
    name: 'Administrator',
    description: 'Full system access and control',
    userCount: 3,
    isSystem: true,
    isActive: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: 2,
    name: 'User',
    description: 'Standard user access for scheduling and basic features',
    userCount: 12,
    isSystem: true,
    isActive: true,
    createdAt: new Date().toISOString(),
  },
];

const STORAGE_KEY = 'scheduler-userRoles';

const getStoredRoles = (): UserRoleListItem[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading user roles from localStorage:', error);
  }
  return defaultMockUserRoles;
};

const saveRoles = (roles: UserRoleListItem[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(roles));
  } catch (error) {
    console.error('Error saving user roles to localStorage:', error);
  }
};

// Mock service for demo mode
const mockUserRoleService = {
  async getAllUserRoles(): Promise<UserRoleListItem[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return getStoredRoles();
  },

  async getUserRoleById(id: number): Promise<UserRole> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const roles = getStoredRoles();
    const role = roles.find(r => r.id === id);
    if (!role) {
      throw new Error(`UserRole with ID ${id} not found`);
    }

    const permissionsKey = `scheduler-userRolePermissions_${id}`;
    let permissions: UserRolePermissions = DEFAULT_ROLE_PERMISSIONS_MAP[id] ?? {};
    try {
      const storedPermissions = localStorage.getItem(permissionsKey);
      if (storedPermissions) {
        permissions = JSON.parse(storedPermissions);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
    }

    return {
      ...role,
      organisationId: 'mock-org-id',
      createdBy: 1,
      permissions,
      users: [],
    };
  },

  async createUserRole(dto: CreateUserRoleDto): Promise<UserRole> {
    await new Promise(resolve => setTimeout(resolve, 500));
    const roles = getStoredRoles();
    const newId = Math.max(...roles.map(r => r.id), 0) + 1;

    const newRole: UserRoleListItem = {
      id: newId,
      name: dto.name,
      description: dto.description,
      isSystem: false,
      isActive: dto.isActive ?? true,
      userCount: 0,
      createdAt: new Date().toISOString(),
    };

    roles.push(newRole);
    saveRoles(roles);

    if (dto.permissions) {
      const permissionsKey = `scheduler-userRolePermissions_${newId}`;
      localStorage.setItem(permissionsKey, JSON.stringify(dto.permissions));
    }

    return {
      ...newRole,
      organisationId: 'mock-org-id',
      createdBy: 1,
      permissions: dto.permissions,
      users: [],
    };
  },

  async updateUserRole(id: number, dto: UpdateUserRoleDto): Promise<UserRole> {
    await new Promise(resolve => setTimeout(resolve, 500));
    const roles = getStoredRoles();
    const index = roles.findIndex(r => r.id === id);

    if (index === -1) {
      throw new Error(`UserRole with ID ${id} not found`);
    }

    const existingRole = roles[index];
    const updatedRole: UserRoleListItem = {
      ...existingRole,
      name: dto.name,
      description: dto.description,
      isActive: existingRole.isSystem ? true : (dto.isActive ?? existingRole.isActive),
    };

    roles[index] = updatedRole;
    saveRoles(roles);

    if (dto.permissions) {
      const permissionsKey = `scheduler-userRolePermissions_${id}`;
      localStorage.setItem(permissionsKey, JSON.stringify(dto.permissions));
    }

    return {
      ...updatedRole,
      organisationId: 'mock-org-id',
      createdBy: 1,
      permissions: dto.permissions,
      users: [],
    };
  },

  async deleteUserRole(id: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    const roles = getStoredRoles();
    const role = roles.find(r => r.id === id);

    if (!role) {
      throw new Error(`UserRole with ID ${id} not found`);
    }

    if (role.isSystem) {
      throw new Error('System roles cannot be deleted');
    }

    if (role.userCount > 0) {
      throw new Error('Cannot delete a role that has users assigned. Please reassign all users to another role before deleting.');
    }

    const filteredRoles = roles.filter(r => r.id !== id);
    saveRoles(filteredRoles);

    const permissionsKey = `scheduler-userRolePermissions_${id}`;
    localStorage.removeItem(permissionsKey);
  },

  async getEmployeesByRoleId(_roleId: number): Promise<UserRoleEmployee[]> {
    await new Promise(resolve => setTimeout(resolve, 300));
    return [
      { id: 1, name: 'John Smith', email: 'john.smith@example.com', roleName: 'Resource Manager', status: 'active', assignedAt: new Date().toISOString() },
      { id: 2, name: 'Sarah Johnson', email: 'sarah.johnson@example.com', roleName: 'Scheduler', status: 'active', assignedAt: new Date().toISOString() },
    ];
  },

  async assignEmployeeToRole(roleId: number, employeeId: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    try {
      const stored = localStorage.getItem('scheduler-employeeRoleAssignments');
      const assignments: Record<string, number> = stored ? JSON.parse(stored) : {};
      assignments[String(employeeId)] = roleId;
      localStorage.setItem('scheduler-employeeRoleAssignments', JSON.stringify(assignments));

      const roles = getStoredRoles();
      const idx = roles.findIndex(r => r.id === roleId);
      if (idx !== -1) {
        roles[idx] = { ...roles[idx], userCount: roles[idx].userCount + 1 };
        saveRoles(roles);
      }
    } catch (error) {
      console.error('Error saving employee role assignment:', error);
    }
  },

  async removeEmployeeFromRole(roleId: number, employeeId: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    try {
      const stored = localStorage.getItem('scheduler-employeeRoleAssignments');
      if (stored) {
        const assignments: Record<string, number> = JSON.parse(stored);
        if (assignments[String(employeeId)] === roleId) {
          delete assignments[String(employeeId)];
          localStorage.setItem('scheduler-employeeRoleAssignments', JSON.stringify(assignments));
        }
      }

      const roles = getStoredRoles();
      const idx = roles.findIndex(r => r.id === roleId);
      if (idx !== -1 && roles[idx].userCount > 0) {
        roles[idx] = { ...roles[idx], userCount: roles[idx].userCount - 1 };
        saveRoles(roles);
      }
    } catch (error) {
      console.error('Error removing employee role assignment:', error);
    }
  },

  async transferEmployeeToRole(fromRoleId: number, toRoleId: number, employeeId: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, 300));
    try {
      const stored = localStorage.getItem('scheduler-employeeRoleAssignments');
      const assignments: Record<string, number> = stored ? JSON.parse(stored) : {};
      delete assignments[String(employeeId)];
      assignments[String(employeeId)] = toRoleId;
      localStorage.setItem('scheduler-employeeRoleAssignments', JSON.stringify(assignments));

      const roles = getStoredRoles();
      const fromIdx = roles.findIndex(r => r.id === fromRoleId);
      if (fromIdx !== -1 && roles[fromIdx].userCount > 0) {
        roles[fromIdx] = { ...roles[fromIdx], userCount: roles[fromIdx].userCount - 1 };
      }
      const toIdx = roles.findIndex(r => r.id === toRoleId);
      if (toIdx !== -1) {
        roles[toIdx] = { ...roles[toIdx], userCount: roles[toIdx].userCount + 1 };
      }
      saveRoles(roles);
    } catch (error) {
      console.error('Error transferring employee role:', error);
    }
  },

  async getMyPermissions(): Promise<MyPermissionsResponse> {
    await new Promise(resolve => setTimeout(resolve, 200));

    // Look up current user's role assignment from localStorage
    const stored = localStorage.getItem('scheduler-employeeRoleAssignments');
    const assignments: Record<string, number> = stored ? JSON.parse(stored) : {};
    // In demo mode, use employee ID '1' as the current user
    const currentUserId = '1';
    const roleId = assignments[currentUserId] ?? null;

    if (roleId) {
      const permissionsKey = `scheduler-userRolePermissions_${roleId}`;
      let permissions: UserRolePermissions = DEFAULT_ROLE_PERMISSIONS_MAP[roleId] ?? {};
      try {
        const storedPerms = localStorage.getItem(permissionsKey);
        if (storedPerms) {
          permissions = JSON.parse(storedPerms);
        }
      } catch {
        // use default
      }
      const roles = getStoredRoles();
      const role = roles.find(r => r.id === roleId);
      return { roleId, roleName: role?.name ?? null, isAdminFallback: false, permissions };
    }

    // No role assigned — return admin fallback permissions (demo mode defaults to admin)
    return { roleId: null, roleName: null, isAdminFallback: true, permissions: ADMINISTRATOR_DEFAULT_PERMISSIONS };
  },
};

// Get the appropriate service based on the data mode
export const getUserRoleService = (mode: DataMode) => {
  return mode === 'live' ? realUserRoleService : mockUserRoleService;
};

// Default export for backward compatibility (uses mock by default)
export const userRoleService = mockUserRoleService;
